import React, { FC, useMemo } from 'react';
import { getIn, useFormikContext } from 'formik';
import { Asset, Button, PlusIcon } from '@clearcover/atlas';

import { useLazyQuoteApi } from 'Shared/services/Api';

import {
  AccidentType,
  FormValuesType,
  ViolationType,
} from 'QuoteApp/services/endpoints/GetDetails';
import {
  CreateIncidentResponseType,
  transformAddIncidentResponse,
} from 'QuoteApp/services/endpoints/PostAddIncident';
import { GetConfigResponseType } from 'QuoteApp/services/endpoints/GetConfig';
import { useAppContext } from 'QuoteApp/services/providers';
import { useEdit } from 'QuoteApp/utils/contexts';
import { IncidentForm } from './IncidentForm';
import styles from './styles.module.css';
import { FormErrorBanner } from '../FormErrorBanner';
import { useQuoteAppFeatureFlags } from 'QuoteApp/utils/hooks';

type IncidentListProps = {
  driverId: string;
  variant: 'accident' | 'violation';
  config: GetConfigResponseType['drivers'];
  alwaysShowErrors: boolean;
};

const variantCopy = {
  accident: {
    header: 'Accidents',
    addButton: 'Add Accident',
  },
  violation: {
    header: 'Violations',
    addButton: 'Add Violation',
  },
};

const NUM_MAX_INCIDENTS = 20;

const emptyAccident: Omit<AccidentType, 'identifier'> = {
  month: '',
  day: '',
  year: '',
  lossType: 'no_losses', // this field comes back as "no_losses" on a new accident and does not accept null due to how CA loss types are calculated
  accidentType: '',
  atFault: '',
  typeOfLoss: '',
};

const emptyViolation: Omit<ViolationType, 'identifier'> = {
  month: '',
  day: '',
  year: '',
  violationType: '',
  sr22: '',
};

const IncidentList: FC<IncidentListProps> = ({ driverId, variant, config }) => {
  const copy = variantCopy[variant];
  const {
    values,
    setFieldValue,
    setStatus,
    errors,
    touched,
  } = useFormikContext<FormValuesType>();

  const {
    collectDayOfAccidentFlag,
    collectTypeOfLossFlag,
  } = useQuoteAppFeatureFlags();

  const { editingId } = useEdit();
  const { appId, details, setDetails } = useAppContext();

  const lookbackYears =
    variant === 'accident'
      ? config.accidents.lookbackYears
      : config.violations.lookbackYears;

  const incidents: Record<string, AccidentType | ViolationType> =
    variant === 'accident'
      ? values.drivers[driverId].accidents
      : values.drivers[driverId].violations;

  const [{ loading: addingChild }, postAddChild] = useLazyQuoteApi<
    CreateIncidentResponseType
  >({
    method: 'POST',
    url: `/insurance_applications/${appId}/drivers/${driverId}/add_${variant}`,
  });

  const isAddIncidentCommandAvailable = useMemo(
    () =>
      details.commands.drivers[driverId].commands?.some(
        c => c.name === `add_${variant}` && c.runnable
      ),
    [details.commands.drivers, driverId, variant]
  );

  const canAddMoreIncidents =
    Object.values(incidents).length < NUM_MAX_INCIDENTS &&
    isAddIncidentCommandAvailable;

  const incidentInFuture = useMemo(
    () =>
      Object.values(incidents).some(incident => {
        const base = `drivers.${editingId}.${variant}s.${incident.identifier}`;
        const incidentDate =
          getIn(values, `${base}.month`) &&
          getIn(values, `${base}.year`) &&
          new Date(
            `${getIn(values, `${base}.month`)} ${getIn(values, `${base}.year`)}`
          );
        return incidentDate >= new Date();
      }),
    [editingId, incidents, values, variant]
  );

  const hasMissingFieldDate = useMemo(
    () =>
      Object.values(incidents).some(incident => {
        const base = `drivers.${editingId}.${variant}s.${incident.identifier}`;
        return (
          (getIn(errors, `${base}.month`) && getIn(touched, `${base}.month`)) ||
          (getIn(errors, `${base}.year`) && getIn(touched, `${base}.year`)) ||
          (collectDayOfAccidentFlag &&
            getIn(errors, `${base}.day`) &&
            getIn(touched, `${base}.day`))
        );
      }),
    [incidents, editingId, variant, errors, touched, collectDayOfAccidentFlag]
  );

  const hasMissingTypeOfLoss = useMemo(() => {
    if (!collectTypeOfLossFlag) return false;

    return Object.values(incidents).some(incident => {
      const base = `drivers.${editingId}.${variant}s.${incident.identifier}`;
      return (
        getIn(errors, `${base}.typeOfLoss`) &&
        getIn(touched, `${base}.typeOfLoss`)
      );
    });
  }, [editingId, incidents, errors, touched, variant, collectTypeOfLossFlag]);

  const missingFieldDateError = useMemo(() => {
    let errorMessage = '';

    if (hasMissingFieldDate) {
      errorMessage = collectDayOfAccidentFlag
        ? 'Month, Day and Year are all required if entered.'
        : 'Month and Year are both required if entered.';
    }

    return errorMessage;
  }, [hasMissingFieldDate, collectDayOfAccidentFlag]);

  return (
    <div className={styles.wrapper}>
      <h4 className={styles.heading}>{copy.header}</h4>
      <p className={styles.subHeader}>
        {variant === 'accident' && config.accidents.collectAccidentType
          ? `Please add any accidents in the last ${lookbackYears} years. Include only at-fault accidents resulting in property damage over $1,000, or bodily injury to anyone involved.`
          : `Please add any ${variant}s in the last ${lookbackYears} years.`}
      </p>
      {Object.keys(incidents).length > 0 && (
        <>
          <ul className={styles.tableBody}>
            {Object.keys(incidents).map(incidentId => {
              return (
                <li key={incidentId} className={styles.tableRowWrapper}>
                  <IncidentForm
                    driverId={driverId}
                    variant={variant}
                    incidentId={incidentId}
                    config={config}
                  />
                </li>
              );
            })}
          </ul>
        </>
      )}
      {editingId === driverId &&
        (hasMissingFieldDate || incidentInFuture || hasMissingTypeOfLoss) && (
          <FormErrorBanner
            message={`${missingFieldDateError}
            ${incidentInFuture ? 'Cannot be a future date.' : ''}
            ${hasMissingTypeOfLoss ? 'Type of loss is required.' : ''}
            `}
          />
        )}
      <div className={styles.addIncidentWrapper}>
        <Button
          link
          size="sm"
          className={styles.addButton}
          data-testid={`add-${variant}-button`}
          isSubmitting={addingChild}
          disabled={
            editingId !== driverId || !canAddMoreIncidents || addingChild
          }
          onClick={async () => {
            const response = await postAddChild({
              transformResponse: transformAddIncidentResponse,
            });
            /* We need to keep track of returned app details in case
             user tries to cancel out of the form, we would then
             set the app details to the most recent returned app details
             to preserve the incidents we have added/removed. */
            setStatus({ newAppDetails: response.data.appDetails });
            setFieldValue(`drivers.${driverId}.${variant}s`, {
              ...incidents,
              [response.data.newIdentifier]: {
                ...(variant === 'accident' ? emptyAccident : emptyViolation),
                identifier: response.data.newIdentifier,
              },
            });
            setDetails({
              ...details,
              commands: response.data.appDetails.commands,
              status: response.data.appDetails.status,
              effectiveDate: response.data.appDetails.effectiveDate,
            });
          }}
        >
          <Asset
            icon={PlusIcon}
            description="add-accident-plus"
            height={10}
            width={10}
          />
          {copy.addButton}
        </Button>
      </div>
    </div>
  );
};

export const AccidentList: FC<Omit<IncidentListProps, 'variant'>> = props => {
  return <IncidentList {...props} variant="accident" />;
};

export const ViolationList: FC<Omit<IncidentListProps, 'variant'>> = props => {
  return <IncidentList {...props} variant="violation" />;
};
